import {
  AttachmentIcon,
  Button,
  DeleteIcon,
  ShareIcon,
  Text
} from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { type ChangeEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { DeleteButton } from '../StepEntryCard'
import AttachmentLink, { QueuedAttachmentLink } from './AttachmentLink'
import {
  ACCEPTED_FILES,
  type AttachmentsValue,
  NO_ATTACHMENTS,
  pickedFilesProblem
} from './caseAttachments'

type AttachmentsFieldProps = {
  /** Injected by the antd `Form.Item`; the label targets the upload button. */
  id?: string
  value?: AttachmentsValue
  onChange?: (value: AttachmentsValue) => void
  testId: string
}

/**
 * Attachments as designed: one row per file, the saved ones linked, each with
 * a Remove, and Upload file below. Picked files wait in the value until the
 * step saves, which uploads them; removed ones are deleted then too.
 */
const AttachmentsField = ({
  id,
  value = NO_ATTACHMENTS,
  onChange,
  testId
}: AttachmentsFieldProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const inputRef = useRef<HTMLInputElement>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const { queued, saved } = value

  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    // Cleared so picking the same file again still fires a change.
    event.target.value = ''
    if (files.length === 0) {
      return
    }
    const found = pickedFilesProblem(files, queued.length)
    setProblem(found && t(found.key, found.values))
    if (!found) {
      onChange?.({ saved, queued: [...queued, ...files] })
    }
  }

  const removeLabel = (name: string) =>
    t(incidentReportsConstants.CASE_V2_ATTACHMENTS_REMOVE, { name })

  return (
    <Field data-testid={testId}>
      {(saved.length > 0 || queued.length > 0) && (
        <Rows>
          {saved.map((file, index) => (
            <Row key={file.key} data-testid={`${testId}-saved-${index}`}>
              <Name>
                <AttachmentIcon size={24} />
                <Text weight="medium">
                  <AttachmentLink file={file} />
                </Text>
              </Name>
              <DeleteButton
                variant="secondary"
                aria-label={removeLabel(file.name)}
                data-testid={`${testId}-saved-${index}-remove`}
                icon={<DeleteIcon size={16} />}
                onClick={() =>
                  onChange?.({
                    queued,
                    saved: saved.filter((other) => other.key !== file.key)
                  })
                }
              />
            </Row>
          ))}
          {queued.map((file, index) => (
            <Row
              // biome-ignore lint/suspicious/noArrayIndexKey: two picks can share a name
              key={`${file.name}-${index}`}
              data-testid={`${testId}-queued-${index}`}
            >
              <Name>
                <AttachmentIcon size={24} />
                <Text weight="medium">
                  <QueuedAttachmentLink file={file} />
                </Text>
              </Name>
              <DeleteButton
                variant="secondary"
                aria-label={removeLabel(file.name)}
                data-testid={`${testId}-queued-${index}-remove`}
                icon={<DeleteIcon size={16} />}
                onClick={() =>
                  onChange?.({
                    saved,
                    queued: queued.filter((other) => other !== file)
                  })
                }
              />
            </Row>
          ))}
        </Rows>
      )}
      {problem && (
        <Text tone="critical" role="alert" data-testid={`${testId}-problem`}>
          {problem}
        </Text>
      )}
      <Upload>
        <Button
          id={id}
          variant="secondary"
          icon={<ShareIcon size={16} />}
          data-testid={`${testId}-upload`}
          onClick={() => inputRef.current?.click()}
        >
          {t(incidentReportsConstants.CASE_V2_ATTACHMENTS_UPLOAD)}
        </Button>
        <HiddenInput
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_FILES}
          tabIndex={-1}
          aria-hidden
          onChange={pick}
          data-testid={`${testId}-input`}
        />
      </Upload>
    </Field>
  )
}

const { color, spacing } = cssVars

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[50]};
`

const Rows = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
`

const Row = styled.li`
  align-items: center;
  display: flex;
  gap: ${spacing[150]};
  padding: ${spacing[100]} 0;
  border-bottom: 1px solid ${color.border.subtle};

  &:last-child {
    border-bottom: 0;
  }
`

/** The icon stays at the top when a long name wraps, as designed. */
const Name = styled.div`
  align-items: flex-start;
  display: flex;
  flex: 1;
  gap: ${spacing[50]};
  min-width: 0;
  overflow-wrap: anywhere;
`

const Upload = styled.div`
  display: flex;
`

/**
 * Out of sight and out of the layout: the button opens it. Not `hidden`,
 * which global input styles can override to show the browser's own button.
 */
const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
  padding: 0;
  margin: -1px;
`

export default AttachmentsField
